import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import QuizSidebarLoader from "./../Loader/QuizSidebarLoader";
import 'react-confirm-alert/src/react-confirm-alert.css';
import classNames from 'classnames';
import { Drawer, Box } from '@material-ui/core';
import QuizSectionMap from "./QuizSectionMap";
import QuizStatus from "./QuizStatus";
import QuizCompactResult from "./QuizCompactResult";
import { useSelector } from "react-redux";
import { useScreenWidth, useStyles } from "sdk/custom-hooks";

const drawerWidth = 320;

function getSectionKey(item, i, counterValue) {
    const sectionId = item?.section?.id || item?.section?._id || item?.section?.en;
    return `${sectionId || 'section'}-${counterValue}-${i}`;
}

/**
 * QuizSidebar
 * @param props
 * @returns {*}
 * @constructor
 */
function QuizSidebar(props) {
    const {
        viewMode, selectPaletteQuestion,
        result, isLoading, timeTaken, changeSectionActive, duration, handler, drawerToggle, startDate, endDate, quiz
    } = props;
    const classes = useStyles(styles)();
    const [ openIndex, setOpenIndex ] = useState(0);
    const questions = useSelector((state) => state.quiz.questions);
    const screenWidth = useScreenWidth();
    const isTabletDrawer = screenWidth > 767 && screenWidth < 1025;
    const shouldUseClosedDrawer = isTabletDrawer ? drawerToggle === false : drawerToggle === true;
    const drawerPaperClass = shouldUseClosedDrawer ? classes.drawerClosed : classes.drawerPaper;

    useEffect(() => {
        if (openIndex !== changeSectionActive) {
            setOpenIndex(changeSectionActive);
        }
    }, [ changeSectionActive, openIndex ]);

    function drawerClose() {
        handler();
    }

    function mobileDrawerClose() {
        if (screenWidth <= 800) {
            handler();
        }
    }

    function handleSectionChange(i) {
        if (openIndex === i) {
            setOpenIndex('');
        } else {
            setOpenIndex(i);
        }
    }

    function renderSidebarContent() {
        if (isLoading) {
            return <Box><QuizSidebarLoader/></Box>;
        }

        let counter = 0;
        return (
            <React.Fragment>
                <QuizCompactResult viewMode={viewMode} questions={questions} timeTaken={timeTaken}
                                   duration={duration} startDate={startDate} endDate={endDate}/>
                <QuizStatus result={result} viewMode={viewMode} questions={questions}/>
                <Box className={classNames(classes.quizSidebarAccordion)}
                     onClick={mobileDrawerClose}>
                    {
                        questions.map((item, i) => {
                            if (i > 0) {
                                counter += questions[i - 1].questions.length;
                            }
                            const sectionKey = getSectionKey(item, i, counter);
                            return (
                                <QuizSectionMap key={sectionKey} index={i} counter={counter} item={item}
                                                selectPaletteQuestion={selectPaletteQuestion}
                                                viewMode={viewMode} openIndex={openIndex} quiz={quiz}
                                                handleSectionChange={handleSectionChange}/>
                            );

                        })
                    }
                </Box>

            </React.Fragment>
        );
    }

    return (
        <Box>
            <Box className={classNames(classes.sidebarIconRight)} onClick={drawerClose}>
                <i className="fa fa-arrow-right"/>
            </Box>
            <Drawer
                className={classes.drawer}
                variant="permanent"
                classes={{
                    paper: drawerPaperClass
                }}
            >
                <Box>
                    <Box className={classes.questionNumberBlock}>
                        <Box className={classNames(classes.sidebarIconLeft)}>
                            <button type="button" onClick={drawerClose} className={classes.iconButton}>
                                <i className="fa fa-arrow-left"/>
                            </button>
                        </Box>
                        {renderSidebarContent()}

                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
}

const styles = theme => ({
    sidebarIconLeft: {
        textAlign: 'right',
        padding: 10,
        color: '#000',
        position: 'absolute',
        top: 16,
        right: 4,
        zIndex: 9,
        '& i': {
            cursor: 'pointer'
        },
        '@media (max-width: 767px)': {
            display: 'none'
        }
    },
    iconButton: {
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        padding: 0,
    },
    quizSidebarAccordion: {
        '@media (max-width: 767px)': {
            marginLeft: 10,
            marginRight: 10,
            marginTop: 50,
            paddingBottom: 30
        },
        '@media (min-width: 280px) and (max-width: 425px)': {
            marginTop: 170,
        },
        '@media (min-width: 426px) and (max-width: 660px)': {
            marginTop: 200,
        },
        '@media (min-width: 661px) and (max-width: 767px)': {
            marginTop: 230,
        },
        '@media (min-width: 768px) and (max-width: 800px)': {
            marginTop: 290,
            position: 'fixed',
            width: '-webkit-fill-available'

        },
        '& > div': {
            boxShadow: 'none',
            marginBottom: '15px !important',
            backgroundColor: '#fff',
        }
    },
    marginTop: {
        marginTop: 10,
    },
    questionNumberBlock: {
        height: 'calc(100vh - 66px)',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
            width: 5,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#d3d5d3'
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#eeecec'
        },
        '@media (max-width: 800px)': {
            height: 'calc(100vh - 50px)',
        }
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
    },
    drawerPaper: {
        zIndex: 0,
        width: drawerWidth,
        top: 0,
        bottom: 0,
        backgroundColor: '#fff',
        borderWidth: 1,
        borderLeftColor: '#ddd',
        height: 'auto',
        overflow: 'hidden',
        transition: '0.3s ease-in-out',

        '@media (max-width:767px)': {
            width: '100%',
            top: 55,
            bottom: 0,
            opacity: 1,
            zIndex: 1000
        },
        '@media (max-width: 1100px) and (min-width: 768px)': {
            zIndex: 99,
        }
    },
    drawerClosed: {
        width: drawerWidth,
        top: 0,
        bottom: 10,
        backgroundColor: '#fff',
        borderWidth: 1,
        borderLeftColor: '#ddd',
        boxShadow: '0 0 15px #aaa',
        height: 'auto',
        transition: '0.3s ease-in-out',
        overflow: 'hidden',
        transform: 'translate(-330px)',
        '@media (max-width:800px)': {
            width: '100%',
            top: 55,
            zIndex: -1000,
            bottom: 0,
            transform: 'unset',
            opacity: 0
        }
    },
    sidebarIconRight: {
        position: 'fixed',
        top: 16,
        left: 0,
        padding: 10,
        backgroundColor: '#fff',
        boxShadow: '0 0 15px #aaa',
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
        cursor: 'pointer',
        '@media(max-width:800px)': {
            display: 'none'
        }
    }
});

QuizSidebar.propTypes = {
    viewMode: PropTypes.bool,
    selectPaletteQuestion: PropTypes.func,
    result: PropTypes.array,
    isLoading: PropTypes.bool,
    timeTaken: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    changeSectionActive: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handler: PropTypes.func,
    drawerToggle: PropTypes.bool,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    quiz: PropTypes.object,
};

export default QuizSidebar;

